/*  Colega Link · Invitar Colegas (v3 · Reto Pionero)
    - Plantillas de mensajes con las ventajas completas: comisión, Colega IA, Biblioteca.
    - Link personalizado: /registro/abogado?ref=[CODIGO]
    - Contador global de cupos Pionero (fetch en vivo desde /api/plans)
    - Código de referido visible para todos los usuarios que ya son Pioneros o Embajadores
*/
import { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { Copy, Check, MessageCircle, Share2, Sparkles, ArrowLeft, Users, Crown, Bot, BookOpen, Trophy } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import PioneroChallengeCard from "@/components/PioneroChallengeCard";

const buildRefUrl = (code) => {
  const origin = (typeof window !== "undefined" && window.location?.origin) || "https://www.colegalink.cl";
  return code ? `${origin}/registro/abogado?ref=${encodeURIComponent(code)}` : `${origin}/registro/abogado`;
};

const TEMPLATES = [
  {
    id: "principal",
    title: "Mensaje principal",
    subtitle: "Tono cercano · ideal para tus primeros 30 colegas",
    icon: MessageCircle,
    accent: "amber",
    build: ({ referralCode, refUrl, signature, slotsLeft }) => `Hola [Nombre] 👋

Te invito a ColegaLink — la red profesional de abogados y mediadores en Chile.

Únete como uno de los *50 Pioneros* y accede a beneficios que después no se repiten:
🤝 *Comisiones preferenciales* (0% en tus primeras 5 delegaciones)
🤖 *Colega IA* — asistente jurídico con Claude Sonnet 4.5 (redacción de escritos, resúmenes, minutas)
📚 *Biblioteca Jurídica* — jurisprudencia validada y plantillas listas
🏆 *Comisión 0% VITALICIA* si completas el Reto Pionero (invitar 15 colegas validados en 90 días)
🛡️ Pago Protegido Colega Link — sin riesgo de quedar mal cobrando

${slotsLeft != null ? `⏳ Solo quedan *${slotsLeft} cupos de Pionero disponibles* — se llenan rápido.\n` : ""}
Regístrate acá 👉 ${refUrl}
${referralCode ? `\n🎟️ Código: *${referralCode}* (ya viene precargado en el link).` : ""}

¿Te tinca? Si me confirmas te ayudo con el paso a paso.

Un abrazo,
${signature}`,
  },
  {
    id: "corto",
    title: "Mensaje corto (segundo contacto)",
    subtitle: "Si el principal no respondió en 48h",
    icon: Sparkles,
    accent: "cyan",
    build: ({ referralCode, refUrl, signature, slotsLeft }) => `[Nombre] 👋 Te reenvío el link por si no lo abriste.

Resumen en 3 líneas:
1. ColegaLink — red confiable de abogados/mediadores + Pago Protegido
2. Solo 50 cupos *Pionero* con Colega IA, Biblioteca Jurídica y comisión 0% vitalicia si cumples el Reto Pionero
3. ${slotsLeft != null ? `Quedan ${slotsLeft}/50 cupos: ` : ""}${refUrl}${referralCode ? `\n\nUsa mi código *${referralCode}* al registrarte.` : ""}

¿Te explico en una llamada de 5 min o lo miras tú primero?

— ${signature}`,
  },
  {
    id: "cercano",
    title: "Colega cercano (modo te necesito)",
    subtitle: "Para amigos directos · alto compromiso",
    icon: Users,
    accent: "emerald",
    build: ({ referralCode, refUrl, signature, slotsLeft }) => `[Nombre], directo al grano:

Lancé ColegaLink y estoy corriendo el *Reto Pionero*: los primeros 50 cupos son para colegas en los que confío. Tú estás en esa lista.

¿Me apoyas? Los beneficios son grandes:
✅ 0% de comisión vitalicia si completo el reto contigo dentro
✅ Colega IA + Biblioteca Jurídica desde el día 1
✅ Insignia PIONERO en tu perfil público

${slotsLeft != null ? `Solo quedan ${slotsLeft} cupos disponibles.\n\n` : ""}Link directo: ${refUrl}${referralCode ? `\nMi código: *${referralCode}*` : ""}

Cualquier duda me preguntas. Y si decides que no, todo bien igual 🙏

— ${signature}`,
  },
  {
    id: "inscritos",
    title: "Inscritos sin completar registro",
    subtitle: "Para usuarios de /inscripcion que no terminaron",
    icon: Crown,
    accent: "violet",
    build: ({ referralCode, refUrl, signature, slotsLeft }) => `Hola [Nombre], vi que te interesaste en ColegaLink 🙌

Antes de que se llenen los ${slotsLeft != null ? `${slotsLeft} cupos que quedan del *Reto Pionero*` : "cupos del *Reto Pionero*"}, quería recordarte lo que te espera:

✅ *Colega IA* — asistente jurídico con Claude Sonnet 4.5
✅ *Biblioteca Jurídica* — jurisprudencia y plantillas
✅ 0% comisión en tus primeras 5 delegaciones
✅ Comisión *0% vitalicia* si cumples el Reto Pionero (15 refs validados en 90 días)
✅ Insignia PIONERO N°XX permanente

Termina tu registro acá: ${refUrl}${referralCode ? `\n🎟️ Código: *${referralCode}*` : ""}

¿Te ayudo con algo?

— ${signature}`,
  },
];

const ACCENT_CLS = {
  amber: { ring: "ring-amber-400/40", bg: "bg-amber-400/5", icon: "text-amber-400" },
  cyan: { ring: "ring-cyan-400/30", bg: "bg-cyan-400/5", icon: "text-cyan-400" },
  emerald: { ring: "ring-emerald-400/30", bg: "bg-emerald-400/5", icon: "text-emerald-400" },
  violet: { ring: "ring-violet-400/30", bg: "bg-violet-400/5", icon: "text-violet-400" },
};

export default function Invitar() {
  const { user } = useAuth();
  const [copied, setCopied] = useState(null);
  const [contactName, setContactName] = useState("");
  const [slotsLeft, setSlotsLeft] = useState(null);

  // Cargar cupos Pionero en vivo
  useEffect(() => {
    let cancelled = false;
    api.get("/plans").then((r) => {
      if (!cancelled) setSlotsLeft(r.data?.pionero_remaining ?? null);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Un usuario puede compartir si es Pionero, Embajador o si tiene referral_code (compatibilidad legacy)
  const canRefer = ["pionero", "embajador_pionero", "founder"].includes(user?.plan) || !!user?.referral_code;

  const params = useMemo(() => {
    const firstName = (user?.name || "").split(" ")[0] || "";
    const referralCode = user?.referral_code || "";
    const refUrl = buildRefUrl(referralCode);
    const signature = user?.name || "Tu colega";
    return { firstName, referralCode, refUrl, signature, slotsLeft };
  }, [user, slotsLeft]);

  const rendered = useMemo(() => {
    return TEMPLATES.map((t) => {
      let body = t.build(params);
      if (contactName.trim()) {
        body = body.replaceAll("[Nombre]", contactName.trim());
      }
      return { ...t, body };
    });
  }, [params, contactName]);

  const copy = async (id, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(id);
      toast.success("Mensaje copiado al portapapeles");
      setTimeout(() => setCopied(null), 2000);
    } catch {
      toast.error("No se pudo copiar. Selecciona el texto manualmente.");
    }
  };

  const shareWhatsApp = (text) => {
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="container-app py-12 max-w-5xl" data-testid="invitar-page">
      <Link to="/app" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 mb-6">
        <ArrowLeft size={14} /> Volver al panel
      </Link>

      <div className="label-eyebrow">Reto Pionero · Comparte por WhatsApp</div>
      <h1 className="serif text-4xl md:text-5xl mt-3 leading-tight">
        Invita colegas <span className="brand-gradient-text">y desbloquea 0% vitalicio.</span>
      </h1>
      <p className="mt-4 text-muted-foreground max-w-2xl leading-relaxed">
        Comparte tu link personalizado. Cuando 15 colegas validados usen tu código y completen su perfil dentro de 90 días,
        te conviertes en <strong className="text-foreground">Pionero Fundador</strong> con 0% de comisión vitalicia.
      </p>

      {/* Progress + slots counter */}
      {slotsLeft != null && (
        <div className="mt-6 p-4 rounded-md border border-amber-400/30 bg-gradient-to-br from-amber-950/20 to-transparent flex items-center gap-3" data-testid="pionero-slots-banner">
          <Trophy size={22} className="text-amber-400 shrink-0"/>
          <div className="flex-1">
            <div className="text-sm text-amber-200">
              <strong className="text-amber-300">Solo quedan {slotsLeft} cupos de Pionero disponibles</strong> · Cuando lleguemos a 0 cierra el programa para siempre.
            </div>
          </div>
        </div>
      )}

      {/* Progress card compacta (misma barra + acciones que el Dashboard) */}
      {canRefer && user?.referral_code && (
        <div className="mt-6" data-testid="invitar-progress">
          <PioneroChallengeCard compact />
        </div>
      )}

      {/* Referral code card */}
      <div className="mt-6 panel p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="referral-card">
        <div className="flex-1">
          <div className="text-xs label-eyebrow">Tu código de referido</div>
          {canRefer && user?.referral_code ? (
            <>
              <div className="mt-1 serif text-2xl text-amber-300" data-testid="referral-code-value">{user.referral_code}</div>
              <div className="mt-1 text-xs text-muted-foreground break-all font-mono" data-testid="referral-url">
                {buildRefUrl(user.referral_code)}
              </div>
            </>
          ) : (
            <div className="mt-1 text-sm text-muted-foreground" data-testid="referral-not-ready">
              {user?.role === "lawyer" ? (
                <>
                  Aún no tienes código de referido. Únete al Reto Pionero para desbloquearlo.{" "}
                  <Link to="/planes" className="underline hover:text-foreground">Ver planes</Link>.
                </>
              ) : (
                <>El programa de referidos es exclusivo para abogados Pioneros.</>
              )}
            </div>
          )}
        </div>
        {canRefer && user?.referral_code && (
          <div className="flex gap-2 shrink-0">
            <button
              onClick={() => copy("code", user.referral_code)}
              className="btn-ghost text-xs inline-flex items-center gap-1.5"
              data-testid="copy-referral-code"
            >
              {copied === "code" ? <Check size={14} /> : <Copy size={14} />}
              {copied === "code" ? "Copiado" : "Copiar código"}
            </button>
            <button
              onClick={() => copy("url", buildRefUrl(user.referral_code))}
              className="btn-ghost text-xs inline-flex items-center gap-1.5"
              data-testid="copy-referral-url"
            >
              {copied === "url" ? <Check size={14} /> : <Copy size={14} />}
              {copied === "url" ? "Copiado" : "Copiar link"}
            </button>
          </div>
        )}
      </div>

      {/* Benefits badges */}
      <div className="mt-6 flex flex-wrap gap-2" data-testid="pionero-benefits">
        <span className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-cyan-400/10 border border-cyan-400/30 text-cyan-200">
          <Bot size={12}/> Colega IA
        </span>
        <span className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-emerald-400/10 border border-emerald-400/30 text-emerald-200">
          <BookOpen size={12}/> Biblioteca Jurídica
        </span>
        <span className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-amber-400/10 border border-amber-400/30 text-amber-200">
          <Trophy size={12}/> 0% comisión vitalicia (si cumples el reto)
        </span>
      </div>

      {/* Contact name personalization */}
      <div className="mt-6 panel p-5" data-testid="contact-name-panel">
        <label className="block">
          <span className="text-xs label-eyebrow">Personalizar con nombre del contacto (opcional)</span>
          <input
            type="text"
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            placeholder='Reemplaza "[Nombre]" en todas las plantillas — ej: "María"'
            className="field mt-2 w-full text-sm"
            data-testid="contact-name-input"
          />
        </label>
        <p className="text-xs text-muted-foreground mt-2">
          Tip: déjalo vacío y reemplázalo a mano en WhatsApp si vas a enviar a varias personas distintas.
        </p>
      </div>

      {/* Templates */}
      <div className="mt-8 space-y-5" data-testid="invitar-templates">
        {rendered.map((t) => {
          const A = ACCENT_CLS[t.accent];
          return (
            <div key={t.id} className={`panel p-6 ring-1 ${A.ring} ${A.bg}`} data-testid={`template-${t.id}`}>
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center bg-background/50 ${A.ring} ring-1`}>
                  <t.icon size={18} className={A.icon} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <h3 className="serif text-xl leading-tight">{t.title}</h3>
                      <p className="text-xs text-muted-foreground mt-0.5">{t.subtitle}</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => copy(t.id, t.body)}
                        className="btn-ghost text-xs inline-flex items-center gap-1.5"
                        data-testid={`copy-${t.id}`}
                      >
                        {copied === t.id ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
                        {copied === t.id ? "Copiado" : "Copiar"}
                      </button>
                      <button
                        onClick={() => shareWhatsApp(t.body)}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium bg-[#25D366] text-white hover:bg-[#1ebe5d] transition-colors"
                        data-testid={`share-${t.id}`}
                      >
                        <Share2 size={13} /> Compartir WhatsApp
                      </button>
                    </div>
                  </div>

                  <div className="mt-4 p-4 rounded-md bg-background/60 border border-white/5 whitespace-pre-wrap text-sm leading-relaxed text-foreground/90 max-h-96 overflow-y-auto" data-testid={`preview-${t.id}`}>
                    {t.body}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Tips */}
      <div className="mt-10 panel p-6 bg-[hsl(var(--card))]/40">
        <h3 className="serif text-2xl">Tips para maximizar tu Reto Pionero</h3>
        <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
          <Tip>
            <strong className="text-foreground">Recuerda: solo hay 50 cupos de Pionero en toda la historia de ColegaLink.</strong>{" "}
            {slotsLeft != null && <span className="text-amber-300">Quedan {slotsLeft} disponibles</span>} — la escasez es real y funciona.
          </Tip>
          <Tip><strong className="text-foreground">Personaliza el nombre</strong> — los mensajes con {"\"[Nombre]\""} sin reemplazar no se leen.</Tip>
          <Tip><strong className="text-foreground">Envía en horario laboral</strong> — entre 09:30–12:00 o 15:00–18:00. Evita lunes a primera hora.</Tip>
          <Tip><strong className="text-foreground">Máx 20 mensajes/hora</strong> — WhatsApp puede marcarte como spam.</Tip>
          <Tip><strong className="text-foreground">Responde en menos de 1h</strong> — la conversión cae 80% si tardas más.</Tip>
          <Tip>
            Si te dicen <em>{"\"más adelante\""}</em> → responde: <span className="text-foreground">
              {"\"Te entiendo. Solo te aviso que cuando se llenen los 50 cupos del Reto Pionero cierro el programa para siempre — Colega IA y Biblioteca sólo pasan a ser Premium ($29.990/mes). ¿Te aviso cuando queden los últimos 10?\""}
            </span>
          </Tip>
          <Tip>
            <strong className="text-foreground">Un referido cuenta como VALIDADO cuando</strong> — se registra usando tu código, verifica su email y completa su perfil al 100%.
          </Tip>
        </ul>
      </div>
    </div>
  );
}

function Tip({ children }) {
  return (
    <li className="flex items-start gap-2">
      <Check size={14} className="text-[hsl(var(--accent))] mt-0.5 shrink-0" />
      <span>{children}</span>
    </li>
  );
}
